import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';
import type {
  ListMyNumbersInput,
  PurchaseNumberInput,
  ReconfigureNumberWebhookInput,
  ReleaseNumberInput,
  SearchAvailableNumbersInput,
} from '@cp3/shared';

export type NumberProvider = 'twilio' | 'telnyx';

export interface AvailableNumberRow {
  phoneNumber: string;
  country: string;
  region: string | null;
  locality: string | null;
  postalCode: string | null;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
  addressRequirements: 'none' | 'any' | 'local' | 'foreign';
  beta: boolean;
  monthlyCost: string | null;
  upfrontCost: string | null;
  currency: string | null;
}

export interface MyNumberRow {
  numberRef: string;
  phoneNumber: string;
  friendlyName: string;
  voiceUrl: string;
  statusCallback: string;
  dateCreated: string;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
  bundleSid: string | null;
  addressSid: string | null;
  connectionId: string | null;
  webhookOwned: boolean;
}

/**
 * Tiny helper: when an admin is viewing `/admin/users/:uid/settings/...`,
 * forward the target uid in the callable payload. The server callable
 * uses `resolveOperatingUid()` to verify admin and act on behalf of
 * the target. Self-mode = no-op.
 */
function useTargetUidPayload(): Record<string, string> {
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  if (adminMode && targetUid) return { uid: targetUid };
  return {};
}

export function useNumberSearch() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: SearchAvailableNumbersInput) => {
      const fn = httpsCallable<
        SearchAvailableNumbersInput & { uid?: string },
        { provider: NumberProvider; items: AvailableNumberRow[] }
      >(functions, 'searchAvailableNumbers');
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
  });
}

export function useMyNumbers(provider: NumberProvider) {
  const adminPayload = useTargetUidPayload();
  return useQuery<{ provider: NumberProvider; items: MyNumberRow[] }>({
    // queryKey carries the target uid so admin-mode and self-mode caches
    // never collide — without this, switching from /settings to
    // /admin/users/X/settings would briefly show stale data.
    queryKey: ['my-numbers', provider, adminPayload.uid ?? null] as const,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<
        ListMyNumbersInput & { uid?: string },
        { provider: NumberProvider; items: MyNumberRow[] }
      >(functions, 'listMyNumbers');
      const r = await fn({ provider, ...adminPayload });
      return r.data;
    },
  });
}

export function usePurchaseNumber() {
  const qc = useQueryClient();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: PurchaseNumberInput) => {
      const fn = httpsCallable<
        PurchaseNumberInput & { uid?: string },
        { ok: true; provider: NumberProvider; numberRef: string; phoneNumber: string }
      >(functions, 'purchaseNumber');
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-numbers'] });
      qc.invalidateQueries({ queryKey: ['phone-settings'] });
    },
  });
}

export function useReleaseNumber() {
  const qc = useQueryClient();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: ReleaseNumberInput) => {
      const fn = httpsCallable<
        ReleaseNumberInput & { uid?: string },
        { ok: true; provider: NumberProvider }
      >(functions, 'releaseNumber');
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-numbers'] });
      qc.invalidateQueries({ queryKey: ['phone-settings'] });
    },
  });
}

export function useReconfigureNumberWebhook() {
  const qc = useQueryClient();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: ReconfigureNumberWebhookInput) => {
      const fn = httpsCallable<
        ReconfigureNumberWebhookInput & { uid?: string },
        { ok: true; provider: NumberProvider; numberRef: string; voiceUrl: string }
      >(functions, 'reconfigureNumberWebhook');
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-numbers'] }),
  });
}
